export const CAMPAIGN = {
  id: 'ashes-of-vespera',
  title: 'Ashes of Vespera',
  genre: 'dark-fantasy vampire-hunter campaign',
  premise: 'Rowan Vale returns to the village that raised them, carrying a hunter’s blade and the two hungers the world would bury with them.',
  themes: ['belonging', 'hunger', 'chosen kin', 'the cost of safety'],
  protagonist: {
    id: 'rowan-vale',
    name: 'Rowan Vale',
    role: 'Lantern-trained hunter',
    appearance: 'A lean hunter with rain-dark hair, old knife scars, and eyes that catch red light too easily.',
    publicIdentity: 'A capable human orphan who returned after years on the road.',
    hiddenTruth: 'Rowan carries both vampire blood and werewolf blood, an inheritance hidden beneath the hunter’s oath.',
    drives: ['Protect Brackenwake', 'Name the truth of their birth', 'Choose what deserves loyalty'],
  },
  acts: [
    {
      id: 1,
      number: 1,
      title: 'A Light That Bites',
      summary: 'A failing warning bell and a missing villager draw Rowan home, where old kindness collides with an unfamiliar hunger.',
      objective: 'Restore Brackenwake’s lantern line and search Greyfen for the missing bell-rider.',
      openingChoice: 'Answer the bell that has no ringer.',
      turningPoint: 'The bell answers to Rowan’s hidden blood, proving the hunter was also the monster in the village’s oldest story.',
      questIds: ['lantern-in-the-mist', 'scars-in-greyfen'],
      locationIds: ['brackenwake', 'greyfen'],
      bossId: null,
      endingPaths: ['uncrowned-dawn', 'crimson-regent', 'ash-and-antler', 'two-throated'],
    },
    {
      id: 2,
      number: 2,
      title: 'Blood at the Altar',
      summary: 'The Red Chapel opens for Rowan alone. Inside, the Red Court offers the truth while the Lantern Covenant works to bury it.',
      objective: 'Break the chapel’s blood ritual and decide whether Magistrate Voss deserves the mercy Rowan was denied.',
      openingChoice: 'Carry a hunter lantern into a place that feeds on light.',
      turningPoint: 'Voss names Rowan as both bloodlines’ last heir, and the first fracture between their human vows begins to glow.',
      questIds: ['chapel-beneath-blood', 'iron-and-incense'],
      locationIds: ['red-chapel', 'ironpass', 'brackenwake'],
      bossId: 'voss-red-magistrate',
      endingPaths: ['crimson-regent', 'two-throated'],
    },
    {
      id: 3,
      number: 3,
      title: 'Kings Under Stone',
      summary: 'Beneath Duskfall, the first hybrid king waits inside Rowan’s bloodline memory while the Ash Pack refuses to abandon the living above.',
      objective: 'End Morcant’s resurrection bargain and unite the scattered pack before civil war consumes the valley.',
      openingChoice: 'Follow the old blood-mark down into the dark.',
      turningPoint: 'Rowan learns both bloodlines began as one wound inflicted by Morcant, not as separate destinies.',
      questIds: ['crowns-beneath-duskfall', 'pack-without-a-heart'],
      locationIds: ['duskfall-crypt', 'greyfen', 'brackenwake'],
      bossId: 'warden-morcant',
      endingPaths: ['ash-and-antler', 'two-throated'],
    },
    {
      id: 4,
      number: 4,
      title: 'The Wolf at the Gate',
      summary: 'Inquisitor Seraphine empties every lantern in Ironpass while Hollow’s exiles prepare a census that could divide—or save—the valley.',
      objective: 'Stop the null confession, free the caged communities, and turn scattered survivors into a willing alliance.',
      openingChoice: 'Cross the mountain after the last lantern goes dark.',
      turningPoint: 'Seraphine’s cruelty is revealed as a desperate attempt to make choice impossible—and Rowan refuses her bleak arithmetic.',
      questIds: ['saint-of-empty-lanterns', 'hollow-ledger'],
      locationIds: ['ironpass', 'hollow', 'greyfen'],
      bossId: 'seraphine-empty-lantern',
      endingPaths: ['uncrowned-dawn', 'ash-and-antler', 'two-throated'],
    },
    {
      id: 5,
      number: 5,
      title: 'A Name for the Night',
      summary: 'Regent Severin gathers the old powers beneath the Red Chapel. Rowan must make one final pact without surrendering the people who followed.',
      objective: 'Defeat the Moonbrand’s human-claimed crown, return the valley to one voice, and name the order Rowan will build.',
      openingChoice: 'Let every surviving faction speak before the red moon rises.',
      turningPoint: 'Rowan can save the valley by becoming its single ruler, or save its freedom by refusing every crown—including the one inside them.',
      questIds: ['two-throats-one-heart', 'the-last-lantern'],
      locationIds: ['red-chapel', 'duskfall-crypt', 'hollow', 'brackenwake'],
      bossId: 'regent-severin',
      endingPaths: ['uncrowned-dawn', 'crimson-regent', 'ash-and-antler', 'two-throated'],
    },
  ],
  bosses: [
    {
      id: 'voss-red-magistrate',
      name: 'Voss',
      title: 'the Red Magistrate',
      act: 2,
      locationId: 'red-chapel',
      questId: 'chapel-beneath-blood',
      factionId: 'red-court',
      form: 'A magistrate with a lantern for a heart and a courthouse packed with blood-ghosts.',
      opening: 'Voss offers Rowan a scales, not a fight: one village life for the life hiding inside Rowan.',
      difficulty: 'story',
      phases: [
        { id: 1, name: 'The Reading', attack: 'Summons mirrored hunters that repeat Rowan’s last movements.', counter: 'Mark the true Voss by the heartbeat under his red collar.' },
        { id: 2, name: 'The Verdict', attack: 'Drains lanterns and feeds Rowan’s two hungers at once.', counter: 'Mara’s litany forces Rowan to hear one chosen heartbeat instead of the whole chapel.' },
      ],
      vulnerabilities: ['named witness', 'broken scales'],
      defeat: 'Voss kneels with the scales still level. His court falls silent for the first time in three centuries.',
      resolutionChoice: 'spare-voss',
      reward: 'red-court-oath-breaker',
    },
    {
      id: 'warden-morcant',
      name: 'Warden Morcant',
      title: 'the first crowned wolf',
      act: 3,
      locationId: 'duskfall-crypt',
      questId: 'crowns-beneath-duskfall',
      factionId: 'moonbrand',
      form: 'A vast antlered revenant, stitched from royal blood and the black roots beneath the crypt.',
      opening: 'Morcant remembers dying Rowan’s bloodline together, then asks them to complete the life he stole.',
      difficulty: 'hard',
      phases: [
        { id: 1, name: 'The Claim', attack: 'Forces Rowan to relive the night the vampire and wolf lines were split.', counter: 'Sable names each memory as a choice rather than a blood command.' },
        { id: 2, name: 'The Root', attack: 'Grows royal antlers fed by fear and splits the pack against itself.', counter: 'Oren rallies the wolves around Rowan’s promise to the living, not the crown.' },
      ],
      vulnerabilities: ['refused inheritance', 'broken root-crown'],
      defeat: 'Morcant’s borrowed body collapses into ash, but the dual bloodline remains whole in Rowan.',
      resolutionChoice: 'refuse-crown',
      reward: 'morcant-first-memory',
    },
    {
      id: 'seraphine-empty-lantern',
      name: 'Seraphine Vey',
      title: 'the Saint of Empty Lanterns',
      act: 4,
      locationId: 'ironpass',
      questId: 'saint-of-empty-lanterns',
      factionId: 'lantern-covenant',
      form: 'A towering lantern-keeper whose halo is a ring of snuffed, screaming wicks.',
      opening: 'Seraphine seals the pass and declares that only a confession without choice can make the valley safe.',
      difficulty: 'hard',
      phases: [
        { id: 1, name: 'The Census', attack: 'Strips every traveler’s name and binds them as anonymous wick-servants.', counter: 'The party speaks each recovered name aloud in defiance of the roll.' },
        { id: 2, name: 'The Silence', attack: 'Empties the pass of sound so hidden hungers can overpower every defender.', counter: 'Mara and Tamsin answer with two incompatible promises, giving the trapped people a choice again.' },
      ],
      vulnerabilities: ['spoken names', 'two wills'],
      defeat: 'Seraphine falls silent at last, and the first living lantern flame relights in her empty hand.',
      resolutionChoice: 'break-census-chain',
      reward: 'ironpass-lantern-order-seal',
    },
    {
      id: 'regent-severin',
      name: 'Regent Severin',
      title: 'the Two-Throated Crown',
      act: 5,
      locationId: 'red-chapel',
      questId: 'two-throats-one-heart',
      factionId: 'moonbrand',
      form: 'A human mask with a crowned wolf behind it, drinking fear from a congregation of willing devotees.',
      opening: 'Severin offers Rowan the vacant Moonbrand throne and swears every atrocity was necessary to make the valley obey one creature.',
      difficulty: 'final',
      phases: [
        { id: 1, name: 'The Edict', attack: 'Compels every marked bloodline to kneel and turns doubt into a crown of thorns.', counter: 'The party’s accumulated promises break the edict by making refusal collective.' },
        { id: 2, name: 'The Apotheosis', attack: 'Wears the chapel’s gathered fear as a two-headed beast.', counter: 'Rowan claims the hunger as theirs, then gives its power back to those who willingly shared it.' },
      ],
      vulnerabilities: ['distributed power', 'severed regalia'],
      defeat: 'Severin’s human mask survives. The wolf behind it does not, leaving Rowan to decide what a person may become.',
      resolutionChoice: 'unmake-crown',
      reward: 'moonbrand-unbroken-seal',
    },
  ],
};

export const FACTIONS = [
  {
    id: 'lantern',
    name: 'Lantern Covenant',
    motto: 'No house stands alone beneath a carried light.',
    leader: 'Prior Elian',
    sigil: 'A seven-wicked lantern crossed by a hunter’s bar.',
    description: 'Monastery wardens, village healers, and covenant-hunters who believe safety is worth a carefully edited truth.',
    agenda: 'Unite the valley beneath a renewed Lantern rite and bury the royal bloodlines beneath Duskfall.',
    methods: ['public ledgers', 'mercy oaths', 'controlled fear', 'quiet omissions'],
    relationshipToPlayer: 'Rowan trained under its hunters, then returned as both a hero and an unrecorded liability.',
    color: '#d9b96f',
  },
  {
    id: 'court',
    name: 'Red Court',
    motto: 'A life freely given is the only honest law.',
    leader: 'Magistrate Voss',
    sigil: 'An empty chalice beneath a red crescent.',
    description: 'Old vampires who keep memory as law and call obedience a form of mercy.',
    agenda: 'Awaken the court beneath the chapel and place a blood heir on the Duskfall throne.',
    methods: ['debts', 'blood witnesses', 'offered shelter', 'precise bargains'],
    relationshipToPlayer: 'The Court recognizes Rowan’s hunger before anyone else and wants them to reclaim it.',
    color: '#b84755',
  },
  {
    id: 'pack',
    name: 'Ash Pack',
    motto: 'We remember the living long after the fire.',
    leader: 'Mother Hessa',
    sigil: 'A white claw crossed through a circle of ash.',
    description: 'Werewolf clans and border hunters who guard Greyfen through scent-memory and kin obligation.',
    agenda: 'Free the scattered packs from the Crown’s commands and make Greyfen a refuge no court can claim.',
    methods: ['scent lines', 'pack witness', 'reciprocal shelter', 'moon challenges'],
    relationshipToPlayer: 'The Pack smelled Rowan’s second nature at Brackenwake and has not decided whether that makes them kin or prey.',
    color: '#9aa5ad',
  },
  {
    id: 'moonbrand',
    name: 'Moonbrand',
    motto: 'Two inherit. One rules.',
    leader: 'Regent Severin',
    sigil: 'Interlocked fangs and antlers around an empty throne.',
    description: 'A forbidden lineage of hunters, blood speakers, and oath-breakers who preserved the history of both bloodlines.',
    agenda: 'Forced by Severin to crown Rowan as a single sovereign and return the valley to royal rule.',
    methods: ['prophetic scars', 'competing testaments', 'blood marks', 'delayed revelation'],
    relationshipToPlayer: 'Moonbrand records call Rowan the only possible bridge, a flattering name for an inherited cage.',
    color: '#8b6f91',
  },
];

export const PARTY = [
  {
    id: 'mara',
    name: 'Mara Quill',
    role: 'Covenant archivist and lantern keeper',
    factionId: 'lantern',
    relationship: 'The friend who remembered Rowan before the village forgot them—and the first to hear both hungers in Rowan’s confession.',
    bond: { label: 'shared-name trust', initial: 1, description: 'Mara keeps Rowan’s true name safe and will challenge the lies that keep them isolated.' },
    ability: { name: 'Litany of Sight', type: 'support', description: 'Names one truthful anchor in a crowded space; reveals hidden routes and interrupts fear-based control.' },
    questIds: ['lantern-in-the-mist', 'iron-and-incense', 'saint-of-empty-lanterns'],
  },
  {
    id: 'oren',
    name: 'Oren Flint',
    role: 'Ash scout and reluctant guide',
    factionId: 'pack',
    relationship: 'A childhood rival who knew Rowan before the blood called; forgiveness remains conditional, but he keeps standing in the same line.',
    bond: { label: 'borrowed lantern', initial: 1, description: 'Oren carries Rowan’s old field lantern and refuses to admit how much its light matters to him.' },
    ability: { name: 'Pack-Sense', type: 'scout', description: 'Reads disturbance in wind, footprints, and distant howls to anticipate enemies and mark safe routes.' },
    questIds: ['scars-in-greyfen', 'pack-without-a-heart', 'two-throats-one-heart'],
  },
  {
    id: 'sable',
    name: 'Sable Rook',
    role: 'Court anatomist turned oath-breaker',
    factionId: 'court',
    relationship: 'The first person to treat Rowan’s blood as something they can master rather than a verdict; trust is built by painful disclosure.',
    bond: { label: 'blood-confidence', initial: 0, description: 'Sable may read Rowan’s bloodline at any distance, but uses that access only with permission.' },
    ability: { name: 'Still Red', type: 'control', description: 'A precise pulse of vampire discipline suppresses predatory impulses and reveals blood-oath links.' },
    questIds: ['chapel-beneath-blood', 'crowns-beneath-duskfall'],
  },
  {
    id: 'tamsin',
    name: 'Tamsin Vane',
    role: 'Moonbrand oath-breaker and seamstress of memory',
    factionId: 'moonbrand',
    relationship: 'Tamsin knows the role written for Rowan and keeps tearing pages out of that fate; affection tastes like a shared rebellion.',
    bond: { label: 'unwritten promise', initial: 0, description: 'Tamsin and Rowan have promised not to become a prophecy without the other’s consent.' },
    ability: { name: 'Unwritten Thread', type: 'support', description: 'Temporarily removes one imposed condition, oath, or recorded destiny from a target or doorway.' },
    questIds: ['hollow-ledger', 'saint-of-empty-lanterns', 'the-last-lantern'],
  },
];

export const LOCATIONS = [
  {
    id: 'brackenwake',
    name: 'Brackenwake',
    region: 'Vale of Vespera',
    description: 'A peat-cut village of low thatch houses, medicinal gardens, and a bell tower rebuilt three times after the river claims it.',
    atmosphere: 'Wet smoke, trampled bracken, and lantern reflections trembling in the flooded lanes.',
    danger: 'Moderate; patrols are honest but fear makes quiet streets dangerous after dusk.',
    questIds: ['lantern-in-the-mist', 'iron-and-incense', 'the-last-lantern'],
    connections: ['greyfen', 'red-chapel'],
  },
  {
    id: 'greyfen',
    name: 'Greyfen Forest',
    region: 'Eastern Fen',
    description: 'A drowned forest of pale trunks where paths remember older borders and the Ash Pack marks safe water with crushed silver needles.',
    atmosphere: 'Cold mist, distant bells beneath the roots, and eyes visible for one breath too long.',
    danger: 'High by night; every familiar scent may belong to a different pack.',
    questIds: ['scars-in-greyfen', 'pack-without-a-heart', 'hollow-ledger'],
    connections: ['brackenwake', 'ironpass', 'hollow'],
  },
  {
    id: 'red-chapel',
    name: 'Red Chapel',
    region: 'Vespera Lowlands',
    description: 'A hilltop chapel with an empty apse, sealed family crypts, and stained glass that shows no living visitor clearly.',
    atmosphere: 'A held breath, hot wax, and the soft chorus of court dead beneath the choir.',
    danger: 'Severe; blood debts rewrite exits and the congregation remembers every promise.',
    questIds: ['chapel-beneath-blood', 'two-throats-one-heart'],
    connections: ['brackenwake', 'duskfall-crypt'],
  },
  {
    id: 'duskfall-crypt',
    name: 'Duskfall Crypt',
    region: 'Old Royal Barrow',
    description: 'A pre-kingdom ossuary beneath a chapelless hill, its corridors rearranged by bloodline and remembered betrayal.',
    atmosphere: 'Root-velvet darkness, cold crowns in the dust, and a heartbeat one chamber ahead of your own.',
    danger: 'Extreme; the dead bargain for memories and royal blood rewrites physical doors.',
    questIds: ['crowns-beneath-duskfall', 'the-last-lantern'],
    connections: ['red-chapel', 'hollow'],
  },
  {
    id: 'ironpass',
    name: 'Ironpass',
    region: 'Northern Ridge',
    description: 'A fortified mountain notch where monastery switchbacks, abandoned mines, and wolf paths meet beneath wind-scoured iron bells.',
    atmosphere: 'Blue snow, strained iron, and lantern light leaning uphill against the wind.',
    danger: 'High; visibility collapses without a carried flame and ambushes favor the high ground.',
    questIds: ['iron-and-incense', 'saint-of-empty-lanterns'],
    connections: ['brackenwake', 'greyfen', 'hollow'],
  },
  {
    id: 'hollow',
    name: 'Hollow',
    region: 'Southern Mire',
    description: 'A half-flooded settlement of stilt houses, communal kitchens, and archive boats where exiles trade news for dry bread.',
    atmosphere: 'Pressed herbs, slow bells, and arguments that pause whenever someone says a banned name.',
    danger: 'Unpredictable; welcomes are genuine, but every faction has listeners beneath the floorboards.',
    questIds: ['hollow-ledger'],
    connections: ['greyfen', 'ironpass', 'duskfall-crypt', 'brackenwake'],
  },
];

export const QUESTS = [
  {
    id: 'lantern-in-the-mist',
    title: 'Lantern in the Mist',
    act: 1,
    locationId: 'brackenwake',
    factionId: 'lantern',
    giver: 'Mara Quill',
    prerequisites: [],
    summary: 'Brackenwake’s warning bell has rung without a hand upon it, and bell-rider Pell has vanished beyond the east road.',
    objectives: ['Speak with Mara at the rebuilt bell tower.', 'Follow the lantern that moves against the wind.', 'Bring Pell—or proof of the road—home before the third bell.'],
    stakes: 'If the warning light dies, the Greyfen dead will cross into the village under cover of the fog.',
    choices: [
      { id: 'ring-the-bell', label: 'Answer with the tower bell', outcome: 'The bell speaks Rowan’s hidden name, but every window in Brackenwake lights in answer.', effects: { flags: { 'bell-answered': true }, bonds: { mara: 1 }, favor: { lantern: 1 }, questId: 'scars-in-greyfen' } },
      { id: 'follow-mara', label: 'Follow Mara’s private trail', outcome: 'Mara admits the bell answer is a bloodline response, and asks Rowan to let her witness what comes next.', effects: { flags: { 'mara-witnessed-blood': true }, bonds: { mara: 1 }, favor: {}, questId: 'scars-in-greyfen' } },
      { id: 'leave-the-road', label: 'Guard the village instead', outcome: 'The search is delayed, but Rowan prevents shadow-things from entering the herbalist’s lane.', effects: { flags: { 'brackenwake-guarded': true }, bonds: {}, favor: { pack: 1 }, questId: 'scars-in-greyfen' } },
    ],
    completion: 'Pell returns alive with a bloodless Moonbrand seal sewn inside his coat; the road has been marked for someone with two hungers.',
    rewards: { flags: { 'pell-returned': true }, bonds: { mara: 1 }, favor: { lantern: 1 }, lore: ['bell-without-ringer'] },
    nextQuestIds: ['scars-in-greyfen'],
  },
  {
    id: 'scars-in-greyfen',
    title: 'Scars in the Greyfen',
    act: 1,
    locationId: 'greyfen',
    factionId: 'pack',
    giver: 'Oren Flint',
    prerequisites: ['lantern-in-the-mist'],
    summary: 'Pell’s trail ends at a drowned shrine where an Ash scout, Oren Flint, is tied beneath roots that tighten whenever he lies.',
    objectives: ['Follow Pell’s seal through the silver-marked paths.', 'Free Oren before the roots mistake Rowan for a Court oath.', 'Reach the old Moonbrand shrine before its first watcher wakes.'],
    stakes: 'The roots will drag Oren beneath the fen, and the shrine will call Rowan by the name the Covenant supposedly burned from every record.',
    choices: [
      { id: 'cut-the-roots', label: 'Cut through the roots', outcome: 'The path opens cleanly, but the Moonbrand seal stains Rowan’s palm in a pattern the Pack recognizes.', effects: { flags: { 'moonbrand-mark': true }, bonds: { oren: 1 }, favor: { pack: 1 }, questId: 'chapel-beneath-blood' } },
      { id: 'offer-a-name', label: 'Give the roots a true name', outcome: 'Oren’s roots loosen around his own childhood name, proving names can be keys without being chains.', effects: { flags: { 'root-name-freed': true }, bonds: { oren: 1 }, favor: { pack: 1 }, questId: 'chapel-beneath-blood' } },
      { id: 'share-the-hunger', label: 'Let the wolf blood answer', outcome: 'The forest opens at once; Oren sees enough of Rowan’s second nature to stop pretending this is only a secret.', effects: { flags: { 'oren-saw-wolf-blood': true }, bonds: { oren: 2 }, favor: { pack: 2 }, questId: 'chapel-beneath-blood' } },
    ],
    completion: 'At the shrine, Rowan’s hunter mark and wolf scar align into the Moonbrand sigil. Across the valley, the Red Chapel begins to ring.',
    rewards: { flags: { 'moonbrand-mark': true, 'greyfen-route-open': true }, bonds: { oren: 2 }, favor: { pack: 1 }, lore: ['two-throats', 'pack-scent-memory'] },
    nextQuestIds: ['chapel-beneath-blood', 'iron-and-incense'],
  },
  {
    id: 'chapel-beneath-blood',
    title: 'The Chapel Beneath Blood',
    act: 2,
    locationId: 'red-chapel',
    factionId: 'court',
    giver: 'Mara Quill',
    prerequisites: ['scars-in-greyfen'],
    summary: 'The Red Chapel has opened its crypts to Rowan alone. Magistrate Voss waits below with scales balanced on the life of Brackenwake.',
    objectives: ['Cross the chapel without surrendering a named villager.', 'Survive Voss’s mirrored witnesses.', 'Decide what justice Rowan will carry out of the chapel.'],
    stakes: 'The Court will either wake beneath the hill or scatter into the villages unless its magistrate is answered.',
    choices: [
      { id: 'enter-openly', label: 'Walk the red aisle as yourself', outcome: 'Voss bows to the hunter and the heir in the same breath; both sides of Rowan step into the record.', effects: { flags: { 'entered-court-openly': true }, bonds: { mara: 1 }, favor: { court: 1 }, questId: 'iron-and-incense' } },
      { id: 'smuggle-sable', label: 'Bring Sable Rook inside the shadow', outcome: 'A defector anatomist recognizes Voss’s scales as a machine for rewriting blood oaths.', effects: { flags: { 'sable-defected': true, 'scales-understood': true }, bonds: { sable: 1 }, favor: { court: 1 }, questId: 'iron-and-incense' } },
      { id: 'spare-voss', label: 'After victory, spare Voss', outcome: 'Voss kneels, not in mercy but in debt. He gives Rowan the Duskfall key and the name Morcant.', effects: { flags: { 'voss-spared': true, 'duskfall-key': true }, bonds: { sable: 1 }, favor: { court: 2 }, questId: 'iron-and-incense' } },
    ],
    completion: 'Voss falls beneath the chapel scales. Sable Rook survives his court, Oren carries the evidence outside, and Mara keeps a hunter honest by naming what the victory cost.',
    rewards: { flags: { 'voss-defeated': true, 'court-riven': true }, bonds: { sable: 1, mara: 1 }, favor: { court: 1 }, lore: ['red-court-oath', 'rowan-two-bloods'] },
    nextQuestIds: ['iron-and-incense'],
  },
  {
    id: 'iron-and-incense',
    title: 'Iron and Incense',
    act: 2,
    locationId: 'ironpass',
    factionId: 'lantern',
    giver: 'Prior Elian',
    prerequisites: ['chapel-beneath-blood'],
    summary: 'A sealed Covenant convoy carries an order to burn Duskfall’s royal tombs. Its wax bears both Rowan’s bloodlines.',
    objectives: ['Climb Ironpass before the convoy reaches the old mine gate.', 'Recover the written order without trusting its single witness.', 'Warn the scattered villages that the Covenant knows Rowan’s secret.'],
    stakes: 'The order names every hybrid child in the valley, turning Rowan’s hidden inheritance into a list for execution.',
    choices: [
      { id: 'warn-villages', label: 'Send the order ahead of the convoy', outcome: 'The truth reaches Hollow, Greyfen, and the mountain settlements before the Covenant can revise the list.', effects: { flags: { 'execution-list-public': true }, bonds: { mara: 1, tamsin: 1 }, favor: { lantern: 1 }, questId: 'crowns-beneath-duskfall' } },
      { id: 'prove-persecution', label: 'Carry proof to Prior Elian', outcome: 'Elian halts the pyre but asks Rowan to surrender their bloodline claims; trust survives only as a probation.', effects: { flags: { 'covenant-briefed': true }, bonds: { mara: 1 }, favor: { lantern: 2 }, questId: 'crowns-beneath-duskfall' } },
      { id: 'burn-the-order', label: 'Burn the Covenant seal', outcome: 'The list is lost, but every settlement now knows Rowan chose secrecy over the Covenant’s protection.', effects: { flags: { 'execution-order-burned': true }, bonds: {}, favor: { pack: 1 }, questId: 'crowns-beneath-duskfall' } },
    ],
    completion: 'The convoy survives, its horses released. From Ironpass, the Duskfall crypt opens beneath a moon that is also, somehow, a red sunrise.',
    rewards: { flags: { 'duskfall-open': true }, bonds: { mara: 1 }, favor: { lantern: 1 }, lore: ['duskfall-crowns', 'covenant-first-light'] },
    nextQuestIds: ['crowns-beneath-duskfall', 'pack-without-a-heart'],
  },
  {
    id: 'crowns-beneath-duskfall',
    title: 'Crowns Beneath Duskfall',
    act: 3,
    locationId: 'duskfall-crypt',
    factionId: 'moonbrand',
    giver: 'Sable Rook',
    prerequisites: ['iron-and-incense'],
    summary: 'Duskfall’s royal dead are standing in their tombs, waiting for the last split bloodline to choose which buried monarch may return.',
    objectives: ['Follow the Duskfall key through shifting royal corridors.', 'Cross Morcant’s memory without accepting his inheritance.', 'Break the root-crown before the court and pack below begin a war.'],
    stakes: 'Morcant’s return would make Rowan the lock and key of both bloodlines, binding every future hybrid to one throne.',
    choices: [
      { id: 'refuse-crown', label: 'Break the root-crown', outcome: 'Morcant remembers Rowan as a child, not an heir, and the ancient obligation loses its oldest name.', effects: { flags: { 'morcant-refused': true, 'inheritance-refused': true }, bonds: { sable: 1, oren: 1 }, favor: { pack: 1 }, questId: 'pack-without-a-heart' } },
      { id: 'carry-the-memory', label: 'Carry Morcant’s first memory', outcome: 'Rowan understands the wound that created both bloodlines, but the dead king’s grief becomes uncomfortably familiar.', effects: { flags: { 'morcant-first-memory': true, 'inheritance-refused': true }, bonds: { sable: 2, tamsin: 1 }, favor: {}, questId: 'pack-without-a-heart' } },
      { id: 'claim-the-threshold', label: 'Claim the threshold briefly', outcome: 'The crypt obeys Rowan, revealing a Moonbrand route to Hollow and a scar that will mark every future ending.', effects: { flags: { 'threshold-claimed': true, 'inheritance-refused': true }, bonds: { sable: 1 }, favor: { court: 1 }, questId: 'pack-without-a-heart' } },
    ],
    completion: 'The royal dead return to dust. Below the bier, Tamsin finds an unwritten page in Tamsin’s own hand, dated tomorrow.',
    rewards: { flags: { 'morcant-defeated': true, 'hollow-route-known': true }, bonds: { sable: 1, tamsin: 1 }, favor: { pack: 1 }, lore: ['rowan-two-bloods', 'moonbrand-two-throats', 'duskfall-crowns'] },
    nextQuestIds: ['pack-without-a-heart', 'saint-of-empty-lanterns'],
  },
  {
    id: 'pack-without-a-heart',
    title: 'A Pack Without a Heart',
    act: 3,
    locationId: 'greyfen',
    factionId: 'pack',
    giver: 'Oren Flint',
    prerequisites: ['crowns-beneath-duskfall'],
    summary: 'The Court’s scattered oaths have turned the Ash Pack against itself. Mother Hessa will not call an alpha while the packs still hear different dead voices.',
    objectives: ['Carry word of Morcant’s fall to every Greyfen camp.', 'Defuse three blood-moon challenges without killing kin.', 'Offer a future the Pack can choose with Rowan.'],
    stakes: 'A divided pack will either fall under the Red Court or expose every settlement they shelter.',
    choices: [
      { id: 'open-table', label: 'Invite every pack to a common fire', outcome: 'Disagreement survives, but for the first time the Pack has a place in Rowan’s plans rather than a leash.', effects: { flags: { 'pack-common-fire': true }, bonds: { oren: 2 }, favor: { pack: 2 }, questId: 'saint-of-empty-lanterns' } },
      { id: 'accept-challenge', label: 'Face the three champions in wolf form', outcome: 'Rowan proves they can bear the wolf hunger without letting it choose, winning respect earned in pain.', effects: { flags: { 'wolf-challenges-survived': true }, bonds: { oren: 2 }, favor: { pack: 2 }, questId: 'saint-of-empty-lanterns' } },
      { id: 'name-mother-hessa', label: 'Ask Hessa to lead the united pack', outcome: 'The Pack gains a firm leader and Rowan releases any claim to stand beside her as equal.', effects: { flags: { 'hessa-is-alpha': true }, bonds: { oren: 1 }, favor: { pack: 1 }, questId: 'saint-of-empty-lanterns' } },
    ],
    completion: 'The Ash Pack becomes one by consent, not blood. Its howl rolls toward Ironpass, where every mountain lantern goes dark.',
    rewards: { flags: { 'ash-pack-united': true }, bonds: { oren: 2 }, favor: { pack: 1 }, lore: ['pack-scent-memory', 'moonbound-marshal'] },
    nextQuestIds: ['saint-of-empty-lanterns'],
  },
  {
    id: 'saint-of-empty-lanterns',
    title: 'The Saint of Empty Lanterns',
    act: 4,
    locationId: 'ironpass',
    factionId: 'lantern',
    giver: 'Mara Quill',
    prerequisites: ['pack-without-a-heart'],
    summary: 'Inquisitor Seraphine has snuffed Ironpass’s lanterns and chained names to a census designed to erase choice. Oren’s united Pack has sent the Moonbound Marshal to hold the pass.',
    objectives: ['Cross the unlit pass by wolf scent and lantern memory.', 'Break the Moonbound Marshal’s linked howl before a charged hound reaches the pilgrims.', 'Break Seraphine’s null confession and free the caged pilgrims before their names become permanent blanks.'],
    stakes: 'The confession will strip the valley of names and bloodlines, creating the obedient people Seraphine believes will survive.',
    choices: [
      { id: 'speak-the-roll', label: 'Speak every stolen name', outcome: 'Pilgrims reclaim themselves one by one; the recovered roll becomes a public covenant against erasure.', effects: { flags: { 'pilgrim-roll-freed': true }, bonds: { mara: 2 }, favor: { lantern: 1 }, questId: 'hollow-ledger' } },
      { id: 'trust-the-wolves', label: 'Trust Oren’s pack-scent through the dark', outcome: 'The united Pack guides the pilgrims out, joining itself to Rowan without surrendering independence.', effects: { flags: { 'pack-carried-the-pilgrims': true }, bonds: { oren: 2 }, favor: { pack: 2 }, questId: 'hollow-ledger' } },
      { id: 'answer-with-two-wills', label: 'Let Mara and Tamsin refuse each other', outcome: 'Their incompatible promises break the confession; harmony is replaced by the more durable gift of dissent.', effects: { flags: { 'two-wills-refused-serenity': true }, bonds: { mara: 1, tamsin: 2 }, favor: {}, questId: 'hollow-ledger' } },
    ],
    completion: 'Seraphine’s census burns. At its heart is a map showing every hollowed lantern, all of them primed to feed one gathering at Hollow.',
    rewards: { flags: { 'seraphine-defeated': true, 'null-census-broken': true }, bonds: { mara: 1, tamsin: 1 }, favor: { lantern: 1 }, lore: ['covenant-first-light', 'names-as-keys'] },
    nextQuestIds: ['hollow-ledger'],
  },
  {
    id: 'hollow-ledger',
    title: 'The Hollow Ledger',
    act: 4,
    locationId: 'hollow',
    factionId: 'moonbrand',
    giver: 'Tamsin Vane',
    prerequisites: ['saint-of-empty-lanterns'],
    summary: 'Hollow’s archive boats contain every name the factions erased. Tamsin can use the ledger to expose Regent Severin—but the truth may consume its readers.',
    objectives: ['Gather four faction witnesses without granting any a final say.', 'Recover the erased Moonbrand history.', 'Turn the settlement census into a mutual-defense pact.'],
    stakes: 'A single ruling power can rewrite the ledger, while an honest record may finally unite the valley against Severin.',
    choices: [
      { id: 'copy-every-name', label: 'Copy every name into every archive', outcome: 'No faction can erase a history it does not possess alone, and Hollow becomes its keeper.', effects: { flags: { 'ledger-copied-everywhere': true }, bonds: { tamsin: 2 }, favor: { pack: 1 }, questId: 'two-throats-one-heart' } },
      { id: 'let-survivors-publish', label: 'Give the ledger to the survivors', outcome: 'The first history of Vespera is written by the people whose lives it describes, not by the factions it indicts.', effects: { flags: { 'survivors-publish-ledger': true }, bonds: { mara: 1, tamsin: 1, oren: 1 }, favor: { lantern: 1 }, questId: 'two-throats-one-heart' } },
      { id: 'unmake-severin', label: 'Unwrite Severin’s return', outcome: 'Tamsin severs the Regent’s foretold coronation, but loses the memory of why Severin first became a friend.', effects: { flags: { 'severin-return-unwritten': true, 'tamsin-memory-taxed': true }, bonds: { tamsin: 1 }, favor: { court: 1 }, questId: 'two-throats-one-heart' } },
    ],
    completion: 'Hollow signs a living compact: no throne, no blood census, no wall between kin and refuge. Every signatory receives a name written in fresh ink.',
    rewards: { flags: { 'hollow-compact-signed': true, 'severin-return-unwritten': true }, bonds: { tamsin: 1 }, favor: { pack: 1 }, lore: ['hollow-exiles', 'names-as-keys'] },
    nextQuestIds: ['two-throats-one-heart'],
  },
  {
    id: 'two-throats-one-heart',
    title: 'Two Throats, One Heart',
    act: 5,
    locationId: 'red-chapel',
    factionId: 'moonbrand',
    giver: 'Rowan Vale',
    prerequisites: ['hollow-ledger'],
    summary: 'Severin crowns the last empty chapel with every fear in Vespera, then offers Rowan the only throne that can bind the bloodlines together.',
    objectives: ['Enter the chapel with the party’s bonds unbroken.', 'Defeat the Regent without accepting his vision of rule.', 'Distribute the crown’s power before it chooses a new bearer.'],
    stakes: 'Refusal risks a bloodline war; acceptance creates one sovereign whose hunger can never be counselled again.',
    choices: [
      { id: 'share-the-crown', label: 'Break the crown among the bonded', outcome: 'Mara, Oren, Sable, and Tamsin hold the power apart long enough for Severin’s mask to break.', effects: { flags: { 'crown-shared': true }, bonds: { mara: 1, oren: 1, sable: 1, tamsin: 1 }, favor: { lantern: 1, court: 1, pack: 1 }, questId: 'the-last-lantern' } },
      { id: 'break-regalia', label: 'Sever every royal regalia', outcome: 'No faction can gather the old powers again, though each resents the loss of its old path to safety.', effects: { flags: { 'regalia-severed': true }, bonds: { oren: 1 }, favor: { pack: 1 }, questId: 'the-last-lantern' } },
      { id: 'unmake-crown', label: 'Make the crown remember no ruler', outcome: 'Severin’s future is unwritten, but Rowan must choose the order that will govern this ungovernable opening.', effects: { flags: { 'crown-unmade': true, 'severin-future-unwritten': true }, bonds: { tamsin: 2 }, favor: { court: 1 }, questId: 'the-last-lantern' } },
    ],
    completion: 'Severin falls. The Red Chapel retains no throne, but dawn finds four living witnesses with four incompatible promises—and no excuse to leave.',
    rewards: { flags: { 'severin-defeated': true, 'red-court-ended': true, 'moonbrand-regalia-broken': true }, bonds: { mara: 1, oren: 1, sable: 1, tamsin: 1 }, favor: { lantern: 1, court: 1, pack: 1 }, lore: ['two-throats', 'moonbrand-two-throats'] },
    nextQuestIds: ['the-last-lantern'],
  },
  {
    id: 'the-last-lantern',
    title: 'The Last Lantern',
    act: 5,
    locationId: 'brackenwake',
    factionId: 'lantern',
    giver: 'Mara Quill',
    prerequisites: ['two-throats-one-heart'],
    summary: 'The factions converge at the rebuilt bell tower. Rowan can claim a crown, seal a bloodline, or return the lantern that began the story to the village.',
    objectives: ['Hear one promise from each faction and the Hollow compact.', 'Tell the people what Rowan’s bloodlines are.', 'Choose the order that will live after Rowan does.'],
    stakes: 'The old powers are broken, but frightened people still need a story to tell about the hunter who came home changed.',
    choices: [
      { id: 'uncrowned-dawn', label: 'Return the lantern to the village', outcome: 'A covenant of many houses replaces four blood crowns. Rowan remains the hunter who made a new kind of oath.', effects: { flags: { 'ending-uncrowned-dawn': true }, bonds: { mara: 2, oren: 1, sable: 1, tamsin: 1 }, favor: { lantern: 2, court: 1, pack: 1 }, questId: null } },
      { id: 'crimson-regent', label: 'Take the Red regalia', outcome: 'Rowan rules as a mortal-court sovereign, making the old hungers answerable to law and never to blood alone.', effects: { flags: { 'ending-crimson-regent': true }, bonds: { sable: 2, tamsin: 1 }, favor: { court: 2, lantern: 1, pack: 1 }, questId: null } },
      { id: 'ash-and-antler', label: 'Let the Ash Pack choose its crown', outcome: 'The old families renew the Pack as chosen kin, protected by Rowan rather than ruled by them.', effects: { flags: { 'ending-ash-and-antler': true }, bonds: { oren: 2, mara: 1 }, favor: { pack: 2, court: 1, lantern: 1 }, questId: null } },
      { id: 'two-throated', label: 'Take the unwritten throne', outcome: 'Rowan rules only by renewing the promise each dawn, making both bloodlines visible and neither one sovereign.', effects: { flags: { 'ending-two-throated': true }, bonds: { tamsin: 2, sable: 1, oren: 1 }, favor: { court: 1, pack: 1, lantern: 1 }, questId: null } },
    ],
    completion: 'At first light, the warning bell is rung by many hands. Whatever Rowan has become, no single throne receives the final call.',
    rewards: { flags: { 'campaign-complete': true }, bonds: {}, favor: { lantern: 1, court: 1, pack: 1 }, lore: ['rowan-two-bloods', 'brackenwake-lantern'] },
    nextQuestIds: [],
  },
];

export const ENDINGS = [
  {
    id: 'uncrowned-dawn',
    title: 'The Uncrowned Dawn',
    route: 'Lantern Covenant',
    trigger: 'Rowan returns the last lantern to Brackenwake and refuses every old crown.',
    summary: 'A dispersed covenant of lanterns replaces the Covenant’s secret hierarchy and the four blood factions’ rule.',
    epilogue: 'Mara edits the first public chronicle; Oren keeps the northern roads; Sable polices dangerous oaths; Tamsin teaches children to question the next prophecy. Rowan wakes each morning human enough to miss someone and hungry enough to understand them. The bell rings because a village decides it is time, not because a bloodline commands it.',
    tone: 'fragile, communal, hopeful',
  },
  {
    id: 'crimson-regent',
    title: 'The Crimson Regent',
    route: 'Red Court',
    trigger: 'Rowan takes the Red regalia and accepts the burden of judging old debts.',
    summary: 'A reformed Court becomes the valley’s public law, ruling from lit rooms where every blood bargain is spoken aloud.',
    epilogue: 'Sable stands beside Rowan whenever hunger sharpens into cruelty. The Court’s sealed families fall one by one, while its shelters grow larger. People call the peace real, and Rowan never corrects them. On quiet nights they listen for the one heartbeat that can still move them; on louder nights they open the doors for those who have nowhere else to go.',
    tone: 'costly, intimate, unsparing',
  },
  {
    id: 'ash-and-antler',
    title: 'Ash and Antler',
    route: 'Ash Pack',
    trigger: 'Rowan yields the succession to the united packs and accepts kin rather than sovereignty.',
    summary: 'Greyfen becomes a living sanctuary governed by pack memory, mutual shelter, and freely renewed challenges.',
    epilogue: 'Oren returns to scout the eastern border but never quite leaves Rowan’s fire. Mara lights Covenant infirmaries within pack territory, and Tamsin severs any oath that claims a wolf’s loyalty. The Court retreats across the river. At full moon, the valley hears a howl and knows it is a greeting rather than a hunt.',
    tone: 'feral, loyal, renewing',
  },
  {
    id: 'two-throated',
    title: 'The Two-Throated Sovereign',
    route: 'Moonbrand',
    trigger: 'Rowan takes the unwritten throne and renews the office with every dawn’s consent.',
    summary: 'For the first time both bloodlines rule visibly, bound by promises that can be challenged, ended, and rewritten.',
    epilogue: 'Tamsin keeps the throne’s unfinished record; Sable interprets the blood laws; Oren guarantees no court or pack can make succession sacred; Mara makes Rowan repeat their promises aloud. The two hungers remain, but neither is denied. When the red moon rises, Rowan stands with the kingdom—not above it—and the bell waits for an answer that is still theirs to give.',
    tone: 'powerful, ambiguous, self-aware',
  },
];

const findById = (items, id) => items.find((item) => String(item.id) === String(id)) ?? null;

export const getAct = (id) => findById(CAMPAIGN.acts, id);
export const getQuest = (id) => findById(QUESTS, id);
export const getLocation = (id) => findById(LOCATIONS, id);
export const getPartyMember = (id) => findById(PARTY, id);
export const getEnding = (id) => findById(ENDINGS, id);
export const getBoss = (id) => findById(CAMPAIGN.bosses, id);

export function getBossForAct(id) {
  const act = getAct(id);
  return act?.bossId ? getBoss(act.bossId) : null;
}

export function createInitialCampaignState() {
  const bonds = {};
  for (const member of PARTY) bonds[member.id] = member.bond.initial;

  return {
    act: 1,
    questId: 'lantern-in-the-mist',
    completedQuestIds: [],
    locationId: 'brackenwake',
    flags: {},
    bonds,
    choices: [],
    discoveredLore: [],
    favor: { lantern: 0, court: 0, pack: 0 },
    upgrades: [],
    checkpoint: 'prologue',
    tutorialStep: 0,
    ending: null,
  };
}

const toRecord = (value) => value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : {};

const uniqueStrings = (value) => Array.isArray(value)
  ? [...new Set(value.filter((item) => typeof item === 'string'))]
  : [];

const numericRecord = (value, defaults) => {
  const source = toRecord(value);
  const result = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (typeof source[key] === 'number' && Number.isFinite(source[key])) result[key] = source[key];
  }
  return result;
};

const booleanFlags = (value) => Object.fromEntries(
  Object.entries(toRecord(value)).filter(([, enabled]) => enabled === true),
);

export function isQuestAvailable(id, campaignState = {}) {
  const quest = getQuest(id);
  if (!quest) return false;
  const completedIds = Array.isArray(campaignState) ? campaignState : campaignState?.completedQuestIds;
  const completed = new Set(uniqueStrings(completedIds));
  return quest.prerequisites.every((prerequisiteId) => completed.has(String(prerequisiteId)));
}
export function isMoonboundMarshalUnlocked(campaignState = {}) {
  const source = toRecord(campaignState);
  const completed = new Set(uniqueStrings(source.completedQuestIds));
  return source.flags?.['ash-pack-united'] === true || completed.has('pack-without-a-heart');
}
export function isMoonboundMarshalDefeated(campaignState = {}) {
  return toRecord(campaignState).flags?.['moonbound-marshal-broken'] === true;
}

// Act II is where the Covenant learns to pull a wick without touching the road: Seraphine is
// named, the pass is emptied, and the first wick-servants walk out onto the Lantern Line. The
// gate is the completed quest rather than a flag, because every one of its three choices sets a
// different flag and none of them is guaranteed.
export function isWickSnufferUnlocked(campaignState = {}) {
  const source = toRecord(campaignState);
  const completed = new Set(uniqueStrings(source.completedQuestIds));
  return completed.has('iron-and-incense') || source.act >= 3;
}

const SIGIL_QUEST_IDS = Object.freeze({
  ember: 'scars-in-greyfen',
  fang: 'iron-and-incense',
  veil: 'the-last-lantern',
});

const FINAL_BOSS_QUEST_ID = CAMPAIGN.bosses[CAMPAIGN.bosses.length - 1]?.questId ?? null;

export function getQuestForSigil(id, collectedSigils = []) {
  const collected = new Set(
    collectedSigils instanceof Set
      ? [...collectedSigils].map(String)
      : uniqueStrings(collectedSigils).map(String),
  );
  if (id !== undefined && id !== null) collected.add(String(id));
  const sigilId = String(id ?? '');
  if (Object.keys(SIGIL_QUEST_IDS).every((key) => collected.has(key))) {
    return getQuest(FINAL_BOSS_QUEST_ID);
  }
  return getQuest(SIGIL_QUEST_IDS[sigilId] ?? null);
}

export function isValidCampaignState(value) {
  const source = toRecord(value);
  const act = getAct(source.act);
  const quest = getQuest(source.questId);
  const location = getLocation(source.locationId);
  if (!act || !quest || !location || String(act.id) !== String(quest.act)) return false;
  if (![source.flags, source.bonds, source.favor].every((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))) return false;
  if (![source.choices, source.discoveredLore, source.upgrades, source.completedQuestIds].every(Array.isArray)) return false;
  if (Object.values(source.flags).some((enabled) => typeof enabled !== 'boolean')) return false;
  if ([...Object.values(source.bonds), ...Object.values(source.favor)].some((value) => typeof value !== 'number' || !Number.isFinite(value))) return false;
  if (typeof source.checkpoint !== 'string' || !Number.isInteger(source.tutorialStep) || source.tutorialStep < 0) return false;
  return source.ending === null || Boolean(getEnding(source.ending));
}

export function migrateCampaignState(value) {
  const base = createInitialCampaignState();
  const source = toRecord(value);
  const requestedQuest = getQuest(source.questId);
  const requestedAct = getAct(source.act);
  const quest = requestedQuest
    ?? requestedAct?.questIds.map((questId) => getQuest(questId)).find(Boolean)
    ?? getQuest(base.questId);
  const act = getAct(quest?.act ?? source.act) ?? getAct(base.act);
  const locationId = getLocation(source.locationId)?.id ?? quest?.locationId ?? base.locationId;
  const flags = booleanFlags(source.flags);

  let completedQuestIds = uniqueStrings(source.completedQuestIds).filter((questId) => getQuest(questId));
  if (!Array.isArray(source.completedQuestIds)) {
    const currentIndex = QUESTS.findIndex((record) => record.id === quest?.id);
    if (currentIndex > 0) completedQuestIds = QUESTS.slice(0, currentIndex).map((record) => record.id);
  }

  const inferredEndingFlag = Object.keys(flags).find((flag) => flag.startsWith('ending-') && flags[flag]);
  const ending = (inferredEndingFlag ? getEnding(inferredEndingFlag.slice(7))?.id : null)
    ?? getEnding(source.ending)?.id
    ?? null;

  return {
    act: act.id,
    questId: quest.id,
    completedQuestIds,
    locationId,
    flags,
    bonds: numericRecord(source.bonds, base.bonds),
    choices: uniqueStrings(source.choices),
    discoveredLore: uniqueStrings(source.discoveredLore),
    favor: numericRecord(source.favor, base.favor),
    upgrades: uniqueStrings(source.upgrades),
    checkpoint: typeof source.checkpoint === 'string' && source.checkpoint ? source.checkpoint : base.checkpoint,
    tutorialStep: Number.isInteger(source.tutorialStep) && source.tutorialStep >= 0 ? source.tutorialStep : base.tutorialStep,
    ending,
  };
}

export function discoverLore(campaignState, id) {
  if (!id || !Array.isArray(campaignState?.discoveredLore) || campaignState.discoveredLore.includes(id)) return false;
  campaignState.discoveredLore.push(id);
  return true;
}

export function resolveEndingId(value) {
  const source = toRecord(value);
  const choices = uniqueStrings(source.choices);
  for (let index = choices.length - 1; index >= 0; index -= 1) {
    if (getEnding(choices[index])) return choices[index];
  }
  const flags = toRecord(source.flags);
  for (const flag of Object.keys(flags).reverse()) {
    if (!flags[flag] || !flag.startsWith('ending-')) continue;
    const id = flag.slice(7);
    if (getEnding(id)) return id;
  }
  if (getEnding(source.ending)) return source.ending;
  const favor = toRecord(source.favor);
  if ((favor.court || 0) > (favor.lantern || 0) && (favor.court || 0) >= (favor.pack || 0)) return 'crimson-regent';
  if ((favor.pack || 0) > (favor.lantern || 0) && (favor.pack || 0) > (favor.court || 0)) return 'ash-and-antler';
  const bonds = toRecord(source.bonds);
  if ((bonds.tamsin || 0) >= 3 && (bonds.sable || 0) >= 2) return 'two-throated';
  return 'uncrowned-dawn';
}

